import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Ad, AdPlacement } from '@/types/ad'

const ADS_COLLECTION = 'ads'

// Get all ads ordered by priority
export async function getAds(): Promise<Ad[]> {
  const snapshot = await getDocs(collection(db, ADS_COLLECTION))

  const ads = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Ad[]

  // Sort client-side to avoid needing a composite index
  ads.sort((a, b) => (a.priority || 0) - (b.priority || 0))
  return ads
}

// Get active ads, optionally filtered by placement, respecting start/end dates
export async function getActiveAds(placement?: AdPlacement): Promise<Ad[]> {
  // Fetch all ads and filter client-side to avoid composite index requirements
  const snapshot = await getDocs(collection(db, ADS_COLLECTION))

  const now = Timestamp.now()

  const ads = snapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }) as Ad)
    .filter((ad) => {
      if (!ad.isActive) return false
      if (placement && ad.placement !== placement) return false
      if (ad.startDate && ad.startDate.toMillis() > now.toMillis()) return false
      if (ad.endDate && ad.endDate.toMillis() < now.toMillis()) return false
      return true
    })

  // Sort by priority ascending
  ads.sort((a, b) => (a.priority || 0) - (b.priority || 0))
  return ads
}

// Create a new ad
export async function createAd(
  data: Omit<Ad, 'id' | 'clicks' | 'impressions' | 'createdAt'>
): Promise<string> {
  const adData = {
    ...data,
    clicks: 0,
    impressions: 0,
    createdAt: serverTimestamp(),
  }

  const docRef = await addDoc(collection(db, ADS_COLLECTION), adData)
  return docRef.id
}

// Update an ad
export async function updateAd(
  id: string,
  data: Partial<Ad>
): Promise<void> {
  const docRef = doc(db, ADS_COLLECTION, id)
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  })
}

// Delete an ad
export async function deleteAd(id: string): Promise<void> {
  const docRef = doc(db, ADS_COLLECTION, id)
  await deleteDoc(docRef)
}

// Record an ad click
export async function recordAdClick(id: string): Promise<void> {
  const docRef = doc(db, ADS_COLLECTION, id)
  await updateDoc(docRef, {
    clicks: increment(1),
  })
}

// Record an ad impression
export async function recordAdImpression(id: string): Promise<void> {
  const docRef = doc(db, ADS_COLLECTION, id)
  await updateDoc(docRef, {
    impressions: increment(1),
  })
}
